export const DIRECTUS_LS_CACHE_PREFIX = 'directus:gql:';

export type CachedGraphQLResponse = {
  expiresAt: number;
  value: unknown;
};

export type PostRequestData = {
  postUrl: string;
  postInit: RequestInit;
  operationName: string | null;
  bodyObj: Record<string, unknown>;
};

function cacheBusterSuffix(cacheBuster?: string | null): string | null {
  const b = String(cacheBuster ?? '').trim();

  return b ? `::${b}` : null;
}

export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);

  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;

  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();

  return `{${keys.map((k) => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',')}}`;
}

export function fnv1aHash(str: string): string {
  let h = 0x811c9dc5;

  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }

  return (h >>> 0).toString(16);
}

export function generateCacheKey(
  operationName: string,
  query: string,
  variables?: unknown,
  extensions?: unknown,
  prefix: string = DIRECTUS_LS_CACHE_PREFIX
): string {
  const v = variables ? stableStringify(variables) : '';
  const e = extensions ? stableStringify(extensions) : '';

  return `${prefix}${operationName}:${fnv1aHash(`${query}|${v}|${e}`)}`;
}

export function jsonResponse(value: unknown): Response {
  return new Response(JSON.stringify(value), {
    status: 200,
    headers: { 'content-type': 'application/json' },
  });
}

export function parseGetUrlForCacheInputs(urlStr: string): {
  operationName: string | null;
  query: string;
  variables?: unknown;
  extensions?: unknown;
} | null {
  try {
    const u = new URL(urlStr);
    const operationName = u.searchParams.get('operationName');
    const query = u.searchParams.get('query') || '';

    const variablesRaw = u.searchParams.get('variables');
    const extensionsRaw = u.searchParams.get('extensions');

    let variables: unknown = undefined;
    let extensions: unknown = undefined;

    if (variablesRaw) {
      try {
        variables = JSON.parse(variablesRaw);
      } catch {
        variables = variablesRaw;
      }
    }

    if (extensionsRaw) {
      try {
        extensions = JSON.parse(extensionsRaw);
      } catch {
        extensions = extensionsRaw;
      }
    }

    return { operationName, query, variables, extensions };
  } catch {
    return null;
  }
}

export function buildPostFromGetUrl(getUrl: string, headers: Headers, init?: RequestInit): PostRequestData {
  const parsed = new URL(getUrl);

  const query = parsed.searchParams.get('query');
  const operationName = parsed.searchParams.get('operationName');
  const variablesRaw = parsed.searchParams.get('variables');
  const extensionsRaw = parsed.searchParams.get('extensions');

  const bodyObj: Record<string, unknown> = {};

  if (query) bodyObj.query = query;

  if (operationName) bodyObj.operationName = operationName;

  if (variablesRaw) {
    try {
      bodyObj.variables = JSON.parse(variablesRaw) as unknown;
    } catch {
      bodyObj.variables = variablesRaw;
    }
  }

  if (extensionsRaw) {
    try {
      bodyObj.extensions = JSON.parse(extensionsRaw) as unknown;
    } catch {
      bodyObj.extensions = extensionsRaw;
    }
  }

  const postHeaders = new Headers(headers);

  if (!postHeaders.has('content-type')) postHeaders.set('content-type', 'application/json');

  const postUrl = parsed.origin + parsed.pathname;

  const postInit: RequestInit = {
    ...init,
    method: 'POST',
    headers: postHeaders,
    body: JSON.stringify(bodyObj),
  };

  return { postUrl, postInit, operationName, bodyObj };
}

export function purgeExpiredDirectusCache(
  prefix: string = DIRECTUS_LS_CACHE_PREFIX,
  cacheBuster?: string | null
): void {
  const now = Date.now();
  const keysToRemove: string[] = [];
  const suffix = cacheBusterSuffix(cacheBuster);

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);

      if (!k || !k.startsWith(prefix)) continue;

      if (suffix && !k.endsWith(suffix)) {
        keysToRemove.push(k);
        continue;
      }

      try {
        const raw = localStorage.getItem(k);

        if (!raw) continue;

        const parsed = JSON.parse(raw) as { expiresAt?: number };

        if (!parsed.expiresAt || parsed.expiresAt <= now) keysToRemove.push(k);
      } catch {
        keysToRemove.push(k);
      }
    }

    for (const k of keysToRemove) localStorage.removeItem(k);
  } catch {
    /* empty */
  }
}

export function readLocalStorageCache(key: string): unknown | null {
  try {
    const raw = localStorage.getItem(key);

    if (!raw) return null;

    const parsed = JSON.parse(raw) as CachedGraphQLResponse;

    if (!parsed.expiresAt || parsed.expiresAt <= Date.now()) {
      localStorage.removeItem(key);

      return null;
    }

    return parsed.value;
  } catch {
    try {
      localStorage.removeItem(key);
    } catch {
      /* empty */
    }

    return null;
  }
}

export function writeLocalStorageCache(key: string, value: unknown, ttlMs: number): void {
  try {
    const payload: CachedGraphQLResponse = {
      expiresAt: Date.now() + ttlMs,
      value,
    };

    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    /* empty */
  }
}

const memoryCache = new Map<string, CachedGraphQLResponse>();

export function readMemoryCache(key: string): unknown | null {
  const entry = memoryCache.get(key);

  if (!entry) return null;

  if (!entry.expiresAt || entry.expiresAt <= Date.now()) {
    memoryCache.delete(key);

    return null;
  }

  return entry.value;
}

export function writeMemoryCache(key: string, value: unknown, ttlMs: number): void {
  memoryCache.set(key, { expiresAt: Date.now() + ttlMs, value });
}

export function purgeExpiredMemoryCache(): void {
  const now = Date.now();

  memoryCache.forEach((v, k) => {
    if (!v.expiresAt || v.expiresAt <= now) {
      memoryCache.delete(k);
    }
  });
}

export function clearMemoryCache(): void {
  memoryCache.clear();
}
